import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'lib/redux/store';

/**
 * Highlight modes supported:
 * - 'word': Highlight specific words/phrases in the resume 
 * - 'sentence': Highlight entire bullet points/statements
 * - 'section': Highlight entire section (e.g., whole experience card) [for future use]
 */
export type HighlightMode = 'word' | 'sentence' | 'section';

/**
 * Section types in the resume
 * Expanded to include all major resume sections
 * 'overall' - Special type for highlighting across ALL sections (e.g., repeated sentences/verbs in Overall section)
 */
export type SectionType = 'experience' | 'education' | 'projects' | 'skills' | 'custom' | 'profile' | 'overall';

/**
 * Mistake types from analysis API
 * Supporting spelling and grammar for Phase 1, can expand later
 */
export type MistakeType = 
  | 'spelling' 
  | 'grammar' 
  | 'buzzword' 
  | 'filler'
  | 'actionVerb'
  | 'passiveVoice';

/**
 * Highlight style configuration for different modes
 */
interface HighlightStyleConfig {
  backgroundColor?: string;
  textDecoration?: string;
  textDecorationStyle?: string;
  textDecorationColor?: string;
  fontWeight?: string;
  color?: string;
}

/**
 * Highlight state interface
 */
interface HighlightState {
  enabled: boolean;                // Global toggle - only true on score-check page
  mode: HighlightMode | null;      // Current highlight mode
  section: SectionType | null;     // Which section to highlight (e.g., 'experience')
  itemIndex: number | null;        // Index of the item within section (e.g., experience[0])
  mistakeType: MistakeType | null; // Type of mistake being highlighted
  mistakeTexts: string[];          // Array of text strings to highlight
  // Mode-specific styles - different visual treatment for word vs sentence vs section
  styles: {
    word: HighlightStyleConfig;    // Style for word-level highlights (spelling, grammar, etc.)
    sentence: HighlightStyleConfig; // Style for sentence-level highlights (long sentences, etc.)
    section: HighlightStyleConfig;  // Style for section-level highlights (entire experience card)
  };
}

/**
 * Initial state - highlighting disabled by default
 * Define distinct visual styles for each highlight mode
 */
const initialState: HighlightState = {
  enabled: false,
  mode: null,
  section: null,
  itemIndex: null,
  mistakeType: null,
  mistakeTexts: [],
  styles: {
    // Word-level highlights: Strong visual treatment for individual mistakes
    word: {
      backgroundColor: '#fff9c4',         // Light yellow background
      textDecoration: 'underline',        // Underline
      textDecorationStyle: 'wavy',        // Wavy underline style
      textDecorationColor: '#d32f2f',     // Red underline color
      fontWeight: 'bold',                 // Bold text
      color: '#d32f2f',                   // Red text color
    },
    // Sentence-level highlights: Softer treatment for entire bullet points
    sentence: {
      backgroundColor: '#ffebee',         // Light pink/red background
      textDecoration: 'underline',        // Underline
      textDecorationStyle: 'solid',       // Solid underline (not wavy)
      textDecorationColor: '#ff5722',     // Orange-red underline
      fontWeight: 'normal',               // Normal weight (not bold)
      color: '#d84315',                   // Dark orange text
    },
    // Section-level highlights: Subtle treatment for entire sections
    section: {
      backgroundColor: '#e3f2fd',         // Light blue background
      textDecoration: 'none',             // No underline
      textDecorationStyle: 'solid',       
      textDecorationColor: 'transparent',
      fontWeight: 'normal',               // Normal weight
      color: 'inherit',                   // Keep original text color
    },
  },
};

/**
 * Highlight slice - manages transient UI state for hover-based highlighting
 * Separate from resume data to avoid polluting the resume slice
 */
const highlightSlice = createSlice({
  name: 'highlight',
  initialState,
  reducers: {
    /**
     * Enable highlighting (called on score-check page mount)
     */
    enableHighlight: (state) => {
      state.enabled = true;
    },

    /**
     * Disable highlighting and reset all state (called on unmount)
     */
    disableHighlight: (state) => {
      state.enabled = false;
      state.mode = null;
      state.section = null;
      state.itemIndex = null;
      state.mistakeType = null;
      state.mistakeTexts = [];
    },

    /**
     * Highlight an entire section (e.g., highlight entire experience card)
     */
    highlightSection: (state, action: PayloadAction<{ 
      section: SectionType; 
      itemIndex: number | null;
    }>) => {
      state.mode = 'section';
      state.section = action.payload.section;
      state.itemIndex = action.payload.itemIndex;
      state.mistakeType = null;
      state.mistakeTexts = [];
    },

    /**
     * Highlight entire sentences/bullet points
     */
    highlightSentences: (state, action: PayloadAction<{
      section: SectionType;
      itemIndex: number | null;
      sentences: string[];
    }>) => {
      state.mode = 'sentence';
      state.section = action.payload.section;
      state.itemIndex = action.payload.itemIndex;
      state.mistakeType = null;
      state.mistakeTexts = action.payload.sentences;
    },

    /**
     * Highlight specific words/phrases (word-level highlighting)
     * This is the primary mode for Phase 1
     */
    highlightMistakes: (state, action: PayloadAction<{
      section: SectionType;
      itemIndex: number | null;
      mistakeType: MistakeType;
      texts: string[];
    }>) => {
      state.mode = 'word';
      state.section = action.payload.section;
      state.itemIndex = action.payload.itemIndex;
      state.mistakeType = action.payload.mistakeType;
      state.mistakeTexts = action.payload.texts;
    },

    /**
     * Clear all highlights (called on mouse leave)
     */
    clearHighlight: (state) => {
      state.mode = null;
      state.section = null;
      state.itemIndex = null;
      state.mistakeType = null;
      state.mistakeTexts = [];
    },
  },
});

// Export actions
export const {
  enableHighlight,
  disableHighlight,
  highlightSection,
  highlightSentences,
  highlightMistakes,
  clearHighlight,
} = highlightSlice.actions;

// Export selectors
export const selectHighlight = (state: RootState) => state.highlight;
export const selectHighlightEnabled = (state: RootState) => state.highlight.enabled;
export const selectHighlightMode = (state: RootState) => state.highlight.mode;
export const selectHighlightTexts = (state: RootState) => state.highlight.mistakeTexts;
export const selectHighlightSection = (state: RootState) => state.highlight.section;
export const selectHighlightItemIndex = (state: RootState) => state.highlight.itemIndex;
// New selector: Get the appropriate style based on current mode
export const selectHighlightStyle = (state: RootState) => {
  const mode = state.highlight.mode;
  if (!mode) return state.highlight.styles.word; // Default to word style
  return state.highlight.styles[mode];
};
export const selectHighlightStyles = (state: RootState) => state.highlight.styles;

// Export reducer
export default highlightSlice.reducer;
